import { COMMA, ENTER, FF_SEMICOLON, SEMICOLON, TAB } from '@angular/cdk/keycodes';
import { Component, ElementRef, forwardRef, Input, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-chip-autocomplete',
  templateUrl: './chip-autocomplete.component.html',
  styleUrls: ['./chip-autocomplete.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ChipAutocompleteComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ChipAutocompleteComponent),
      multi: true
    }
  ]
})

export class ChipAutocompleteComponent implements OnInit, ControlValueAccessor {
  @Input("item-value") ItemValue: Function = (item) => item;
  @Input("item-description") ItemDescription: Function = (item) => item;
  @Input("item-created") ItemAdd: Function = (item) => item;
  @Input("item-descriptionSel") ItemDescriptionSel: Function = (item) => this.ItemDescription(item);
  @Input("data-source") dataSource: any[] = [];
  @Input("data-validation") validateNewItem: Function = (item) => (item || '') === '' || this._filter(item)?.length > 0;
  @Input("control-value") ControlValue: Function = (item: any[]) => item?.map(i => this.ItemValue(i));
  @Input("auto-create") autoCreate = false;

  @Input() placeholder: string;
  @Input() visible = true;
  @Input() selectable = true;
  @Input() removable = true;
  @Input() disabled = false;

  @ViewChild('chipList') chipList;
  @ViewChild('textInput') textInput: ElementRef<HTMLInputElement>;
  @ViewChild('auto') matAutocomplete: MatAutocomplete;

  formControl: FormControl;

  onChange: any = () => { }
  onTouch: any = () => { }

  val: any = null; // this is the updated value that the class accesses

  get value() {
    return this.val;
  }

  set value(val) {  // this value is updated by programmatic changes if( val !== undefined && this.val !== val){
    this.val = val;
    this.onChange(val);
    this.onTouch(val);
  }

  private onDestroy = new Subject<void>();
  separatorKeysCodes: number[] = [ENTER, COMMA, SEMICOLON, FF_SEMICOLON, TAB];

  itemCtrl = new FormControl();
  selectedItems: any[] = [];
  public filteredItems: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);

  ngOnChanges(changes: SimpleChanges) {
    //if (changes.dataSource.currentValue != this.dataSource) {
    let filter = this.itemCtrl.value?.toString();
    //if (this.chipList) this.checkValidation(filter);
    this.filteredItems.next(this._filter(filter));
    //}
  }

  constructor() {

    this.itemCtrl.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        let filter = this.itemCtrl.value?.toString();
        if (this.chipList) this.checkValidation(filter)
        return this.filteredItems.next(this._filter(filter));
      });
  }

  ngOnInit() {
    //this.filteredItems.next(this.dataSource.slice());
  }

  checkValidation(value) {
    let _v = value;
    const isValid = (!_v || _v === '') || this.validateNewItem(value);

    if (this.chipList.errorState != !isValid) {
      if (isValid) this.formControl?.setErrors(null);
      else this.formControl?.setErrors({
        invalid: true
      });
    }

    this.chipList.errorState = !isValid;
    return isValid;
  }

  validate(control: FormControl) {
    this.formControl = control;
    if (this.textInput && this.chipList) {
      return !this.checkValidation(this.textInput.nativeElement.value)
        && {
        invalid: true
      }
    }
    return null;
  }

  writeValue(obj: any): void {
    this.selectedItems = [];
    if (!obj) return;
  
    const aux = Array.isArray(obj)
      ? obj
      : obj.toString()
          .split(/[,;]/)
          .map(item => Number(item.trim())) 
          .filter(value => !isNaN(value)); 
  
    aux.forEach(value => {
      const matchedItem = this.dataSource?.find(item => this.ItemValue(item) === value);
      if (matchedItem) {
        this.selectedItems.push(matchedItem);
      }
    });
  
    this.updateControl();
  }
  
  
  

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  add(event): void {
    const input = event.input;
    const value = event.value;
    if (value && value != "") {
      this.selectOrAdd(value, input);
    }
    // Clear the input value
    if (input) {
      input.value = '';
    }

    this.updateControl();
  }

  addOnBlur(event: FocusEvent) {
    if (!this.matAutocomplete.isOpen) {
      const target: HTMLElement = (event.relatedTarget ?? event.currentTarget) as HTMLElement;
      if (!target || target.tagName !== 'MAT-OPTION') {
        const matChipEvent: MatChipInputEvent = { input: this.textInput.nativeElement, value: this.textInput.nativeElement.value } as MatChipInputEvent;
        this.add(matChipEvent);
      }
    }
    setTimeout(() => {
      // Reset the input value
      if (this.textInput) {
        this.textInput.nativeElement.value = '';
      }
      this.itemCtrl.setValue(null);
    }, 500);
  }

  selectOrAdd(value, input = null) {
    let validated = true;
    const new_item = this._filter(value);

    if (!this.selectedItems) this.selectedItems = [];
    // Add our item
    if (new_item?.length > 0) {
      let item_add = new_item.find(x => this.ItemValue(x) === value);
      this.selectedItems.push(item_add);
    } else if (this.autoCreate) {
      const add_item = this.ItemAdd(value);
      if (this.validateNewItem(add_item)) {
        this.selectedItems.push(add_item);
        this.dataSource.push(add_item);
      } else validated = false;
    } else validated = false;

    if (validated) {
      // Reset the input value
      if (input) {
        input.value = '';
      }
      this.itemCtrl.setValue(null);
    }
  }

  remove(item: any): void {
    const index = this.selectedItems.indexOf(item);

    if (index >= 0) {
      this.selectedItems.splice(index, 1);
    }
    this.updateControl();
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    // Reset the input value
    if (this.textInput) {
      this.textInput.nativeElement.value = '';
    }
    this.itemCtrl.setValue(null);

    this.selectOrAdd(event.option.value);
    this.updateControl();
  }

  private _isSelected(item) {
    return this.selectedItems?.filter(si => this.ItemValue(item) == this.ItemValue(si)).length > 0;
  }

  private _filter(value: string): any[] {
    let filterValue = (value?.toString() || "")?.toLowerCase().trim();
    if (filterValue == "") filterValue = null;

    return this.dataSource?.filter(item =>
      !this._isSelected(item)
      && (
        !filterValue || (
          this.ItemDescription(item)?.toLowerCase().indexOf(filterValue) !== -1 ||
          this.ItemValue(item)?.toString() === filterValue))
    );
  }

  updateControl() {
    this.value = this.ControlValue(this.selectedItems);
  }
}
